import React, { useState } from 'react';
import { FaCreditCard, FaLock, FaCalendarAlt } from 'react-icons/fa';
import { DashboardApi } from '../../../../apis/DashboardApi';
import { ToastContainer, toast } from 'react-toastify';

interface PaymentMethodFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    card_number: '',
    expiry_date: '',
    cvv: '',
    card_holder_name: ''
  });
  const [errors, setErrors] = useState({
    card_number: '',
    expiry_date: '',
    cvv: '',
    card_holder_name: ''
  });
  const [loading, setLoading] = useState(false);

  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  // Format expiry date (MM/YY)
  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    
    if (v.length > 2) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }
    
    return value;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Format based on field
    if (name === 'card_number') {
      formattedValue = formatCardNumber(value);
    } else if (name === 'expiry_date') {
      formattedValue = formatExpiryDate(value);
    }
    
    setFormData({
      ...formData,
      [name]: formattedValue
    });
    
    // Clear error when typing
    if (errors[name as keyof typeof errors]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {
      card_number: '',
      expiry_date: '',
      cvv: '',
      card_holder_name: ''
    };
    let isValid = true;

    // Validate card number (remove spaces for validation)
    const cardNumber = formData.card_number.replace(/\s/g, '');
    if (!cardNumber) {
      newErrors.card_number = 'Card number is required';
      isValid = false;
    } else if (!/^\d{15,16}$/.test(cardNumber)) {
      newErrors.card_number = 'Invalid card number';
      isValid = false;
    }

    // Validate expiry date
    if (!formData.expiry_date) {
      newErrors.expiry_date = 'Expiry date is required';
      isValid = false;
    } else if (!/^\d{2}\/\d{2}$/.test(formData.expiry_date)) {
      newErrors.expiry_date = 'Invalid format (MM/YY)';
      isValid = false;
    } else {
      // Check if the date is in the future
      const [month, year] = formData.expiry_date.split('/');
      const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);
      const now = new Date();
      
      if (expiryDate < now) {
        newErrors.expiry_date = 'Card has expired';
        isValid = false;
      }
    }

    // Validate CVV
    if (!formData.cvv) {
      newErrors.cvv = 'CVV is required';
      isValid = false;
    } else if (!/^\d{3,4}$/.test(formData.cvv)) {
      newErrors.cvv = 'Invalid CVV';
      isValid = false;
    }

    // Validate card holder name
    if (!formData.card_holder_name.trim()) {
      newErrors.card_holder_name = 'Cardholder name is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare data for the API
      const cardData = {
        card_number: formData.card_number.replace(/\s/g, ''),
        expiry_date: formData.expiry_date,
        cvv: formData.cvv
      };
      
      const response:any = await DashboardApi.updatePaymentMethod(cardData);
      
      if (response.data.status === 'success') {
        toast.success('Payment method updated successfully');
        onSuccess();
      } else {
        toast.error(response.data.message || 'Failed to update payment method');
      }
    } catch (error: any) {
      console.error('Error updating payment method:', error);
      toast.error(error.response?.data?.message || 'Failed to update payment method');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold mb-4">Update Payment Method</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Card Number */}
          <div>
            <label htmlFor="card_number" className="block text-sm font-medium text-gray-700">
              Card Number
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaCreditCard className="text-gray-400" />
              </div>
              <input
                type="text"
                id="card_number"
                name="card_number"
                value={formData.card_number}
                onChange={handleInputChange}
                placeholder="1234 5678 9012 3456"
                maxLength={19}
                className={`pl-10 block w-full border rounded-md py-2 px-3 ${
                  errors.card_number ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.card_number && (
              <p className="mt-1 text-sm text-red-600">{errors.card_number}</p>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Expiry Date */}
            <div>
              <label htmlFor="expiry_date" className="block text-sm font-medium text-gray-700">
                Expiry Date
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaCalendarAlt className="text-gray-400" />
                </div>
                <input
                  type="text"
                  id="expiry_date"
                  name="expiry_date"
                  value={formData.expiry_date}
                  onChange={handleInputChange}
                  placeholder="MM/YY"
                  maxLength={5}
                  className={`pl-10 block w-full border rounded-md py-2 px-3 ${
                    errors.expiry_date ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.expiry_date && (
                <p className="mt-1 text-sm text-red-600">{errors.expiry_date}</p>
              )}
            </div>
            
            {/* CVV */}
            <div>
              <label htmlFor="cvv" className="block text-sm font-medium text-gray-700">
                CVV
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="text-gray-400" />
                </div>
                <input
                  type="text"
                  id="cvv"
                  name="cvv"
                  value={formData.cvv}
                  onChange={handleInputChange}
                  placeholder="123"
                  maxLength={4}
                  className={`pl-10 block w-full border rounded-md py-2 px-3 ${
                    errors.cvv ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.cvv && (
                <p className="mt-1 text-sm text-red-600">{errors.cvv}</p>
              )}
            </div>
          </div>
          
          {/* Card Holder Name */}
          <div>
            <label htmlFor="card_holder_name" className="block text-sm font-medium text-gray-700">
              Card Holder Name
            </label>
            <input
              type="text"
              id="card_holder_name"
              name="card_holder_name"
              value={formData.card_holder_name}
              onChange={handleInputChange}
              placeholder="John Doe"
              className={`block w-full border rounded-md py-2 px-3 ${
                errors.card_holder_name ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.card_holder_name && (
              <p className="mt-1 text-sm text-red-600">{errors.card_holder_name}</p>
            )}
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : 'Update Payment Method'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PaymentMethodForm;